import type React from "react";

import { AIChipButton } from "@similarweb/ai-kit";
import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { TRIAL_BANNER_HEIGHT } from "app/components/React/TrialBanner/StyledTrialBanner";
import { AIAgentCaption } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentCaption";
import GradientButton from "app/pages/sales-intelligence/modules/ai-agent/components/GradientButton/GradientButton";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { AgentKnowledgeBase } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/AgentKnowledgeBase";
import { SuperAgentBreadcrumbs } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentBreadcrumbs/SuperAgentBreadcrumbs";
import { PARTICLES } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentGatePageVariant/constants";
import { SuperAgentTextarea } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentTextarea/SuperAgentTextarea";
import { EXTENDED_AI_SUGGESTIONS } from "app/pages/sales-intelligence/modules/company-autocomplete/components/HomePageAutocomplete/constants";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { LowQuotaBannerContainer } from "app/pages/sales-intelligence/modules/quota/containers/LowQuotaBannerContainer/LowQuotaBannerContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

import { PageWrapper, Particle, ParticleContainer, SlideUpContent } from "./styles";

type Props = {
    value: string;
    setValue: (value: string) => void;
    handleSubmit: () => void;
};

export const SuperAgentGatePageVariant: React.FC<Props> = ({ value, setValue, handleSubmit }) => {
    const { translate } = useTranslate();

    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);

    const isTrialBannerVisible = isTrialOrAfterTrial();
    const topOffset = isTrialBannerVisible ? TRIAL_BANNER_HEIGHT : 0;

    const handleSuggestionClick = (suggestion: string) => {
        setValue(suggestion);
        AIAgentTrackingService.superAgent.trackGatePageSuggestionClick(suggestion);
    };

    return (
        <PageWrapper>
            <ParticleContainer>
                {PARTICLES.map((particle, index) => (
                    <Particle
                        key={index}
                        {...particle}
                    />
                ))}
            </ParticleContainer>
            <Stack
                margin="0 0 0 189px"
                alignItems="center"
                minHeight={isTrialBannerVisible ? `calc(100vh - ${TRIAL_BANNER_HEIGHT}px)` : "100vh"}
                justifyContent="space-between"
            >
                <AgentKnowledgeBase top={topOffset + 12}>
                    <DataCreditsIndicatorContainer />
                </AgentKnowledgeBase>
                <SuperAgentBreadcrumbs variant="compact" />
                <Box width="724px">
                    <SlideUpContent delay={500}>
                        <Text
                            fontVariant="h3"
                            margin="0 0 56px 0"
                            display="block"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("si.ai-super-agent.welcome.title.1", { name: swSettings.user.firstname })}{" "}
                            &nbsp;👋
                            <br />
                            {translate("si.ai-super-agent.welcome.title.2")}
                        </Text>
                    </SlideUpContent>
                    <SlideUpContent delay={700}>
                        <SuperAgentTextarea
                            value={value}
                            onChange={(value: string) => setValue(value)}
                            placeholder={translate("si.ai-super-agent.textarea.placeholder")}
                            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                if (event.key === "Enter" && !event.shiftKey && value) {
                                    event.preventDefault();
                                    handleSubmit();
                                }
                            }}
                            actionButton={
                                <GradientButton
                                    variant={value ? "submit" : "disabled"}
                                    onClick={handleSubmit}
                                    disabled={!value}
                                />
                            }
                            isDisabled={isBlocked}
                        />
                    </SlideUpContent>

                    <SlideUpContent delay={900}>
                        {isBlocked ? null : (
                            <Flex
                                height="48px"
                                gap="8px"
                                alignItems="center"
                                justifyContent="center"
                                margin="16px 0 0 0"
                            >
                                {EXTENDED_AI_SUGGESTIONS.map((suggestion) => (
                                    <AIChipButton
                                        key={suggestion.value}
                                        label={translate(suggestion.label)}
                                        onClick={() => handleSuggestionClick(translate(suggestion.value))}
                                        variant="soft"
                                    />
                                ))}
                            </Flex>
                        )}
                        <Box margin="24px 0 0 0">
                            <LowQuotaBannerContainer />
                        </Box>
                    </SlideUpContent>
                </Box>
                <AIAgentCaption />
            </Stack>
        </PageWrapper>
    );
};
